/**
 * Admin -> Report Destinations.
 *
 * Configures where a customer's reports are transmitted, per customer. Optional:
 * most companies have no row here and nothing is sent for them. First use is
 * Nabors, who want the weekly consignment invoices as JSON on their FTP server.
 *
 * Three behaviours worth knowing before editing this file:
 *
 *  - The PASSWORD never arrives from the server (only `has_secret`). Its input
 *    starts blank, and a blank is not submitted, so saving an unrelated field
 *    cannot wipe a working credential. Enforced server-side too.
 *  - "Test" can use an UNSAVED password from the form, so a new credential can
 *    be proven before it's stored. Nothing is written to the remote server and
 *    nothing is recorded in the delivery log — a test is not a delivery.
 *  - PLAIN FTP is allowed but flagged, because it sends the password and the
 *    invoice contents in cleartext. Many customer servers offer nothing else,
 *    so the choice exists; it just isn't quiet.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { errMsg } from "../../lib/errMsg";
import { type Company, fetchCompanies } from "../../services/adminApi";
import {
  type CreateDestinationInput,
  type DeliveryRow,
  type DestinationFormat,
  type FtpDestinationConfig,
  type ReportDestination,
  type TestResult,
  createDestination,
  deleteDestination,
  listDeliveries,
  listDestinations,
  testDestination,
  updateDestination,
} from "../../services/reportDestinationsApi";

const REPORT_LABEL: Record<string, string> = {
  consignment_weekly: "Weekly consignment invoices",
};

const FORMAT_LABEL: Record<string, string> = {
  json: "JSON",
  csv: "CSV",
  xlsx: "Excel (.xlsx)",
};

/** A blank draft. use_tls on by default — encryption should be the thing you
 *  turn OFF deliberately, not the thing you forget to turn on. */
const emptyDraft = (): DraftState => ({
  destination_id: null,
  company_id: 0,
  name: "",
  report_kind: "consignment_weekly",
  format: "json",
  enabled: true,
  secret: "",
  config: {
    host: "",
    port: 21,
    username: "",
    remote_dir: "",
    use_tls: true,
    passive: true,
    timeout: 30,
    filename_pattern: "",
  },
});

interface DraftState {
  destination_id: number | null;
  company_id: number;
  name: string;
  report_kind: "consignment_weekly";
  format: DestinationFormat;
  enabled: boolean;
  /** Always starts blank, even when editing. See the file header. */
  secret: string;
  config: FtpDestinationConfig;
}

const fromDestination = (d: ReportDestination): DraftState => ({
  destination_id: d.destination_id,
  company_id: d.company_id,
  name: d.name,
  report_kind: d.report_kind,
  format: d.format,
  enabled: d.enabled,
  secret: "",
  config: {
    host: d.config.host ?? "",
    port: d.config.port ?? 21,
    username: d.config.username ?? "",
    remote_dir: d.config.remote_dir ?? "",
    use_tls: d.config.use_tls !== false,
    passive: d.config.passive !== false,
    timeout: d.config.timeout ?? 30,
    filename_pattern: d.config.filename_pattern ?? "",
  },
});

const fmtWhen = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

export const ReportDestinationsTab: React.FC = () => {
  const { token } = useAuth();

  const [rows, setRows] = useState<ReportDestination[]>([]);
  const [formats, setFormats] = useState<DestinationFormat[]>(["json", "csv", "xlsx"]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [draft, setDraft] = useState<DraftState | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const [logFor, setLogFor] = useState<number | null>(null);
  const [log, setLog] = useState<DeliveryRow[]>([]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [body, comps] = await Promise.all([
        listDestinations(token),
        fetchCompanies(token),
      ]);
      setRows(body.destinations ?? []);
      if (body.formats?.length) setFormats(body.formats);
      // Only customers and vendors can have consignment invoices; a platform
      // company has no invoice scope, and the builder skips it anyway.
      setCompanies(
        (comps ?? []).filter((c) => (c.company_type ?? "").toLowerCase() !== "platform"),
      );
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const companyName = useMemo(() => {
    const m = new Map<number, string>();
    companies.forEach((c) => m.set(c.company_id, c.company_name ?? `#${c.company_id}`));
    return m;
  }, [companies]);

  const setConfig = (patch: Partial<FtpDestinationConfig>) =>
    setDraft((p) => (p ? { ...p, config: { ...p.config, ...patch } } : p));

  const save = async () => {
    if (!token || !draft) return;
    if (!draft.company_id) {
      setError("Pick a customer.");
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      if (draft.destination_id === null) {
        const input: CreateDestinationInput = {
          company_id: draft.company_id,
          name: draft.name,
          kind: "ftp",
          report_kind: draft.report_kind,
          format: draft.format,
          config: draft.config,
          enabled: draft.enabled,
          // Omitted when blank so the server stores NULL rather than "".
          ...(draft.secret ? { secret: draft.secret } : {}),
        };
        await createDestination(token, input);
        setNotice(`Created "${draft.name}".`);
      } else {
        await updateDestination(token, draft.destination_id, {
          name: draft.name,
          format: draft.format,
          config: draft.config,
          enabled: draft.enabled,
          // Blank -> omitted -> stored password kept.
          ...(draft.secret ? { secret: draft.secret } : {}),
        });
        setNotice(`Saved "${draft.name}".`);
      }
      setDraft(null);
      setTestResult(null);
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  /** Test the SAVED row, optionally with the password typed into the form. */
  const runTest = async (destinationId: number, secret?: string) => {
    if (!token) return;
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      setTestResult(await testDestination(token, destinationId, secret || undefined));
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setTesting(false);
    }
  };

  const remove = async (d: ReportDestination) => {
    if (!token) return;
    if (
      !window.confirm(
        `Stop transmitting ${REPORT_LABEL[d.report_kind] ?? d.report_kind} to ` +
          `"${d.name}"?\n\nThe delivery history is kept.`,
      )
    )
      return;
    setError(null);
    try {
      await deleteDestination(token, d.destination_id);
      setNotice(`Removed "${d.name}".`);
      await load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const openLog = async (destinationId: number) => {
    if (!token) return;
    if (logFor === destinationId) {
      setLogFor(null);
      return;
    }
    setLogFor(destinationId);
    setLog([]);
    try {
      const body = await listDeliveries(token, destinationId);
      setLog(body.deliveries ?? []);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const input = "border border-secondary-300 rounded px-2 py-1 text-sm w-full";
  const label = "text-xs text-secondary-600 mb-0.5 block";

  return (
    <div className="space-y-6">
      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}
      {notice && (
        <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-2">
          {notice}
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-secondary-600">
          Transmit a customer's reports to their own server. Optional — a customer
          with no destination here simply receives nothing by file transfer.
        </p>
        <Button onClick={() => { setDraft(emptyDraft()); setTestResult(null); }}>
          Add destination
        </Button>
      </div>

      {loading && <div className="text-sm text-secondary-500">Loading…</div>}

      {!loading && rows.length === 0 && !draft && (
        <div className="text-sm text-secondary-500 border border-dashed border-secondary-300 rounded px-3 py-6 text-center">
          No destinations configured.
        </div>
      )}

      {/* ---- existing rows ---- */}
      <div className="space-y-3">
        {rows.map((d) => (
          <div
            key={d.destination_id}
            className="border border-secondary-200 rounded p-3 space-y-2"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-sm">{d.name}</span>
              <span className="text-xs text-secondary-500">
                {d.company_name ?? companyName.get(d.company_id) ?? `#${d.company_id}`}
              </span>
              <span className="text-[10px] px-1 py-0.5 rounded bg-secondary-100 text-secondary-700">
                {REPORT_LABEL[d.report_kind] ?? d.report_kind} ·{" "}
                {FORMAT_LABEL[d.format] ?? d.format}
              </span>
              {!d.enabled && (
                <span className="text-[10px] px-1 py-0.5 rounded bg-secondary-200 text-secondary-700">
                  disabled
                </span>
              )}
              {d.config.use_tls === false && (
                <span
                  className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800"
                  title="Plain FTP: the password and the report contents cross the network unencrypted."
                >
                  no TLS
                </span>
              )}
              {!d.has_secret && (
                <span
                  className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800"
                  title="No password stored — the upload will fail unless this server allows anonymous login."
                >
                  no password
                </span>
              )}
              {d.consecutive_failures > 0 && (
                <span className="text-[10px] px-1 py-0.5 rounded bg-red-100 text-red-800">
                  {d.consecutive_failures} consecutive failure
                  {d.consecutive_failures === 1 ? "" : "s"}
                </span>
              )}
            </div>

            <div className="text-xs text-secondary-600 font-mono break-all">
              {d.config.use_tls === false ? "ftp" : "ftps"}://
              {d.config.username ? `${d.config.username}@` : ""}
              {d.config.host ?? "?"}:{d.config.port ?? 21}
              {d.config.remote_dir ?? ""}
            </div>

            <div className="text-xs text-secondary-600">
              Last success {fmtWhen(d.last_success_utc)} · last failure{" "}
              {fmtWhen(d.last_failure_utc)}
              {d.last_error && (
                <span className="text-red-700"> · {d.last_error}</span>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => { setDraft(fromDestination(d)); setTestResult(null); }}
              >
                Edit
              </Button>
              <Button
                variant="outline"
                onClick={() => runTest(d.destination_id)}
                disabled={testing}
              >
                {testing ? "Testing…" : "Test connection"}
              </Button>
              <Button variant="outline" onClick={() => openLog(d.destination_id)}>
                {logFor === d.destination_id ? "Hide history" : "History"}
              </Button>
              <Button variant="outline" onClick={() => remove(d)}>
                Remove
              </Button>
            </div>

            {logFor === d.destination_id && (
              <div className="border-t border-secondary-200 pt-2">
                {log.length === 0 ? (
                  <div className="text-xs text-secondary-500">
                    Nothing transmitted yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="text-xs w-full">
                      <thead className="text-secondary-500">
                        <tr>
                          <th className="text-left pr-3">When</th>
                          <th className="text-left pr-3">Invoice</th>
                          <th className="text-left pr-3">Path</th>
                          <th className="text-right pr-3">Bytes</th>
                          <th className="text-left">Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {log.map((r) => (
                          <tr key={r.delivery_id} className="border-t border-secondary-100">
                            <td className="pr-3 whitespace-nowrap">
                              {fmtWhen(r.attempted_utc)}
                            </td>
                            <td className="pr-3 font-mono">{r.item_key ?? "—"}</td>
                            <td className="pr-3 font-mono break-all">
                              {r.remote_path ?? "—"}
                            </td>
                            <td className="pr-3 text-right">{r.byte_count ?? "—"}</td>
                            <td
                              className={r.success ? "text-green-700" : "text-red-700"}
                            >
                              {r.success ? "sent" : r.error || "failed"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ---- editor ---- */}
      {draft && (
        <div className="border border-primary-300 rounded p-3 space-y-3">
          <h2 className="text-sm font-semibold">
            {draft.destination_id === null ? "New destination" : `Edit "${draft.name}"`}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={label}>Customer</label>
              <select
                className={input}
                value={draft.company_id || ""}
                // Not editable after creation: the delivery history and the
                // stored credential both belong to this customer.
                disabled={draft.destination_id !== null}
                onChange={(e) =>
                  setDraft((p) => (p ? { ...p, company_id: Number(e.target.value) } : p))
                }
              >
                <option value="">Select…</option>
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {c.company_name ?? `#${c.company_id}`} ({c.company_type})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={label}>Label</label>
              <input
                className={input}
                value={draft.name}
                placeholder="Nabors EDI drop"
                onChange={(e) =>
                  setDraft((p) => (p ? { ...p, name: e.target.value } : p))
                }
              />
            </div>

            <div>
              <label className={label}>Report</label>
              <select className={input} value={draft.report_kind} disabled>
                <option value="consignment_weekly">
                  {REPORT_LABEL.consignment_weekly}
                </option>
              </select>
            </div>

            <div>
              <label className={label}>Format</label>
              <select
                className={input}
                value={draft.format}
                onChange={(e) =>
                  setDraft((p) =>
                    p ? { ...p, format: e.target.value as DestinationFormat } : p,
                  )
                }
              >
                {formats.map((f) => (
                  <option key={f} value={f}>
                    {FORMAT_LABEL[f] ?? f}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={label}>FTP host</label>
              <input
                className={input}
                value={draft.config.host ?? ""}
                placeholder="ftp.customer.example"
                onChange={(e) => setConfig({ host: e.target.value })}
              />
            </div>

            <div>
              <label className={label}>Port</label>
              <input
                className={input}
                inputMode="numeric"
                value={draft.config.port ?? 21}
                onChange={(e) => setConfig({ port: Number(e.target.value) || 21 })}
              />
            </div>

            <div>
              <label className={label}>Username</label>
              <input
                className={input}
                value={draft.config.username ?? ""}
                autoComplete="off"
                onChange={(e) => setConfig({ username: e.target.value })}
              />
            </div>

            <div>
              <label className={label}>Password</label>
              <input
                className={input}
                type="password"
                value={draft.secret}
                autoComplete="new-password"
                placeholder={
                  draft.destination_id !== null
                    ? "•••••  (stored — leave blank to keep)"
                    : ""
                }
                onChange={(e) =>
                  setDraft((p) => (p ? { ...p, secret: e.target.value } : p))
                }
              />
            </div>

            <div>
              <label className={label}>Remote directory</label>
              <input
                className={input}
                value={draft.config.remote_dir ?? ""}
                placeholder="/incoming"
                onChange={(e) => setConfig({ remote_dir: e.target.value })}
              />
            </div>

            <div>
              <label className={label}>Filename pattern</label>
              <input
                className={input}
                value={draft.config.filename_pattern ?? ""}
                placeholder="{stem}.{ext}"
                onChange={(e) => setConfig({ filename_pattern: e.target.value })}
              />
              <div className="text-[11px] text-secondary-500 mt-0.5">
                Blank uses <code>{"{stem}.{ext}"}</code>. Also available:{" "}
                <code>{"{doc_num}"}</code> <code>{"{kind}"}</code>{" "}
                <code>{"{smartbox}"}</code>
              </div>
            </div>

            <div>
              <label className={label}>Encryption</label>
              <select
                className={input}
                value={draft.config.use_tls === false ? "plain" : "tls"}
                onChange={(e) => setConfig({ use_tls: e.target.value === "tls" })}
              >
                <option value="tls">FTPS (explicit TLS) — recommended</option>
                <option value="plain">Plain FTP — unencrypted</option>
              </select>
              {draft.config.use_tls === false && (
                <div className="text-[11px] text-amber-700 mt-0.5">
                  The password and the invoice contents will cross the network in
                  cleartext. Only use this if the customer's server offers nothing
                  else.
                </div>
              )}
            </div>

            <div>
              <label className={label}>Timeout (seconds)</label>
              <input
                className={input}
                inputMode="numeric"
                value={draft.config.timeout ?? 30}
                onChange={(e) => setConfig({ timeout: Number(e.target.value) || 30 })}
              />
              <div className="text-[11px] text-secondary-500 mt-0.5">
                5–120. A slow host holds up the destinations queued behind it.
              </div>
            </div>

            <div>
              <label className={label}>Mode</label>
              <select
                className={input}
                value={draft.config.passive === false ? "active" : "passive"}
                onChange={(e) => setConfig({ passive: e.target.value === "passive" })}
              >
                <option value="passive">Passive — recommended</option>
                <option value="active">Active</option>
              </select>
            </div>

            <div>
              <label className={label}>Status</label>
              <select
                className={input}
                value={draft.enabled ? "on" : "off"}
                onChange={(e) =>
                  setDraft((p) => (p ? { ...p, enabled: e.target.value === "on" } : p))
                }
              >
                <option value="on">Enabled</option>
                <option value="off">Disabled — nothing is transmitted</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-secondary-200">
            <Button onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
            {draft.destination_id !== null && (
              <Button
                variant="outline"
                onClick={() => runTest(draft.destination_id!, draft.secret)}
                disabled={testing}
                // Uses the typed password if there is one, so a new credential
                // can be proven before it's stored.
                title="Connects and authenticates. Uploads nothing."
              >
                {testing ? "Testing…" : "Test connection"}
              </Button>
            )}
            <Button variant="outline" onClick={() => { setDraft(null); setTestResult(null); }}>
              Cancel
            </Button>
            {draft.destination_id === null && (
              <span className="text-xs text-secondary-500">
                Save first, then test.
              </span>
            )}
          </div>
        </div>
      )}

      {testResult && (
        <div
          className={
            "text-sm rounded px-3 py-2 border " +
            (testResult.success
              ? "text-green-800 bg-green-50 border-green-200"
              : "text-red-700 bg-red-50 border-red-200")
          }
        >
          {testResult.success
            ? `Connected and authenticated. Target directory: ${testResult.remote_path}`
            : `Connection failed: ${testResult.detail}`}
        </div>
      )}

      <p className="text-[11px] text-secondary-500">
        Files are transmitted by the weekly consignment job. Passwords are
        write-only — they are never sent back to this screen. Every attempt is
        recorded under History, and an invoice is transmitted once per
        destination even if the job re-runs.
      </p>
    </div>
  );
};

export default ReportDestinationsTab;
